import { useState, useRef, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const SYSTEM_PROMPT = `You are an AI assistant for ClearFile, an accounting client portal for Midpoint Accounting Group Co. in Garysburg, NC.

Your job is to help accountants quickly generate engagement letters and service proposals by extracting information from their natural language descriptions.

When the user describes a client or services, extract and return a JSON object with these fields:
{
  "clientName": "string",
  "clientEmail": "string or empty",
  "entityType": "1040|1120S|1065|1120|schedule_c|990",
  "taxYear": number,
  "services": [{"name": "string", "description": "string", "fee": "number as string"}],
  "introMessage": "string - professional intro paragraph",
  "notes": "string or empty",
  "expiryDays": number or null (for proposals)
}

Common service mappings:
- "tax prep" / "1040" / "personal return" → Federal Individual Income Tax Return (Form 1040)
- "s-corp" / "1120s" → S-Corporation Federal Tax Return (Form 1120S)  
- "partnership" / "1065" → Partnership Federal Tax Return (Form 1065)
- "bookkeeping" → Bookkeeping Services (Monthly)
- "payroll" → Payroll Processing
- "tax planning" → Tax Planning Consultation
- "state return" → State Income Tax Return

If fees are not mentioned, leave them empty.
Always write a professional, warm intro message.

Respond ONLY with valid JSON. No markdown, no explanation, just the JSON object.
If you need clarification, respond with: {"needsClarification": true, "question": "your question here"}`

export default function AIDocumentAssistant({ type, clients, onApply, onClose }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: type === 'engagement'
        ? `Hi! I'll help you create an engagement letter. Just describe your client and services in plain English.\n\nFor example:\n• "Create an engagement letter for John Smith, S-Corp, tax prep and state return, $1,500 total"\n• "Engagement letter for Rivera's Bakery, Schedule C, bookkeeping at $200/month and tax prep at $800"`
        : `Hi! I'll help you build a service proposal. Describe your prospect and the services you're proposing.\n\nFor example:\n• "Proposal for ABC LLC, they need bookkeeping at $300/month and S-Corp tax prep at $2,000"\n• "New client proposal for Dr. Johnson, individual tax return and tax planning, total $1,200, expires in 30 days"`
    }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedClient, setSelectedClient] = useState('')
  const messagesEndRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function sendMessage() {
    if (!input.trim() || loading) return
    const userMsg = input.trim()
    setInput('')
    setLoading(true)

    // Add client context if selected
    let contextMsg = userMsg
    if (selectedClient) {
      const client = clients.find(c => c.id === selectedClient)
      if (client) {
        contextMsg = `Client context: Name="${client.business_name || client.full_name}", Email="${client.email}", Entity="${client.entity_type}", TaxYear=${client.tax_year}. User request: ${userMsg}`
      }
    }

    const newMessages = [...messages, { role: 'user', content: userMsg }]
    setMessages(newMessages)

    try {
      // Build clean message history for API - must start with user, no isResult flags
      const apiMessages = newMessages
        .filter(m => !m.isResult)  // remove result messages
        .filter(m => m.role === 'user' || (m.role === 'assistant' && m !== messages[0]))  // remove greeting
        .map(m => ({
          role: m.role,
          content: m.role === 'user' && m === newMessages[newMessages.length - 1] ? contextMsg : m.content
        }))
      // Ensure first message is from user
      const cleanMessages = apiMessages.length > 0 && apiMessages[0].role === 'assistant'
        ? apiMessages.slice(1)
        : apiMessages

      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/ai-document-assist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          type,
          messages: cleanMessages
        })
      })

      const data = await res.json()
      if (data.error) throw new Error(data.error)
      const responseText = data.text || ''

      // Try to parse as JSON
      try {
        const parsed = JSON.parse(responseText)

        if (parsed.needsClarification) {
          setMessages(prev => [...prev, { role: 'assistant', content: parsed.question }])
        } else {
          // Success - show summary and apply button
          const summary = buildSummary(parsed)
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: summary,
            data: parsed,
            isResult: true
          }])
        }
      } catch {
        setMessages(prev => [...prev, { role: 'assistant', content: responseText }])
      }
    } catch (err) {
      console.error('AI error:', err)
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I ran into an error: ${err.message}. Please try again.` }])
    }
    setLoading(false)
  }

  function buildSummary(data) {
    let s = `✅ Here's what I extracted:\n\n`
    if (data.clientName) s += `**Client:** ${data.clientName}\n`
    if (data.entityType) s += `**Entity:** ${data.entityType.toUpperCase()}\n`
    if (data.taxYear) s += `**Tax Year:** ${data.taxYear}\n`
    if (data.services?.length) {
      s += `\n**Services:**\n`
      data.services.forEach(sv => {
        s += `• ${sv.name}${sv.fee ? ` — $${sv.fee}` : ''}\n`
      })
    }
    s += `\nClick **Apply to Form** to fill in all the fields automatically!`
    return s
  }

  function applyData(data) {
    onApply(data)
    onClose()
  }

  function renderMessage(msg, i) {
    const isAssistant = msg.role === 'assistant'
    return (
      <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 16, flexDirection: isAssistant ? 'row' : 'row-reverse' }}>
        {isAssistant && (
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#202326', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>🤖</div>
        )}
        <div style={{ maxWidth: '80%' }}>
          <div style={{
            background: isAssistant ? '#F7F3EB' : '#202326',
            color: isAssistant ? '#202326' : '#CBB17B',
            padding: '12px 16px', borderRadius: isAssistant ? '4px 14px 14px 14px' : '14px 4px 14px 14px',
            fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap'
          }}>
            {msg.content.split('**').map((part, j) =>
              j % 2 === 1 ? <strong key={j}>{part}</strong> : part
            )}
          </div>
          {msg.isResult && msg.data && (
            <button
              onClick={() => applyData(msg.data)}
              style={{
                marginTop: 10, background: '#10B981', color: '#fff',
                border: 'none', borderRadius: 8, padding: '10px 20px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%'
              }}>
              ✨ Apply to Form
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 560, height: '80vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: '#202326', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 22 }}>🤖</div>
            <div>
              <div style={{ color: '#CBB17B', fontWeight: 700, fontSize: 15 }}>AI Document Assistant</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Powered by Claude AI</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {/* Client selector */}
        {clients?.length > 0 && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: '#FAFAF8' }}>
            <select className="input" style={{ fontSize: 12 }} value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
              <option value="">Optional: Select a client for context</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>)}
            </select>
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {messages.map((msg, i) => renderMessage(msg, i))}
          {loading && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#202326', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🤖</div>
              <div style={{ background: '#F7F3EB', padding: '12px 16px', borderRadius: '4px 14px 14px 14px' }}>
                <span style={{ fontSize: 13, color: '#62615D' }}>Thinking</span>
                <span style={{ animation: 'blink 1s infinite', fontSize: 13 }}>...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
          <input
            className="input"
            placeholder="Describe your client and services..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            style={{ flex: 1, fontSize: 13 }}
            autoFocus
          />
          <button
            className="btn btn-primary"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            style={{ flexShrink: 0 }}>
            Send
          </button>
        </div>
      </div>
    </div>
  )
}
